import { ColumnMapping, ExcelProcessorConfig, HeaderConfig, ParsedExcelData, ValidationError } from '@/lib/excel';
import { NhaCungCapInput } from '@/types/schemas';

const HEADERS = {
  CUSTOMER_CODE: 'Mã khách hàng*',
  CUSTOMER_NAME: 'Tên khách hàng*',
  ALTERNATIVE_NAME: 'Tên khác',
  IS_CUSTOMER: 'Khách hàng',
  IS_VENDOR: 'Nhà cung cấp',
  ADDRESS: 'Địa chỉ',
  TAX_CODE: 'Mã số thuế',
  CONTACT_PERSON: 'Người liên hệ',
  SALES_REP_CODE: 'Nhân viên bán hàng',
  ACCOUNT_CODE: 'Tài khoản ngầm định',
  PAYMENT_TERM_CODE: 'Mã th.toán công nợ',
  CREDIT_LIMIT: 'Giới hạn tiền nợ',
  BIRTH_DATE: 'Ngày sinh',
  CUSTOMER_GROUP1_CODE: 'Nhóm 1',
  CUSTOMER_GROUP2_CODE: 'Nhóm 2',
  CUSTOMER_GROUP3_CODE: 'Nhóm 3',
  REGION_CODE: 'Khu vực',
  PHONE: 'Điện thoại',
  FAX: 'Fax',
  EMAIL: 'Thư',
  WEBSITE: 'Trang chủ',
  NOTES: 'Ghi chú',
  BANK_ACCOUNT: 'Số tài khoản',
  BANK_NAME: 'Tên ngân hàng',
  BANK_BRANCH: 'Chi nhánh',
  PROVINCE_CODE: 'Tỉnh thành',
  USE_EINVOICE: 'Sử dụng hđ điện tử',
  CUSTOMER_TYPE: 'Loại khách hàng',
  PAYMENT_METHOD_CODE: 'Ph/th th.toán (HĐĐT)',
  EINVOICE_EMAIL: 'Thư nhận HĐĐT',
  LEGAL_REPRESENTATIVE: 'Người đại diện',
  ID_NUMBER: 'Số CMND/CCCD',
  REPRESENTATIVE_POSITION: 'Chức vụ',
  DESCRIPTION: 'Mô tả'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.CUSTOMER_CODE, key: 'customer_code', type: 'main' },
  { header: HEADERS.CUSTOMER_NAME, key: 'customer_name', type: 'main' },
  { header: HEADERS.ALTERNATIVE_NAME, key: 'alternative_name', type: 'main' },
  { header: HEADERS.IS_CUSTOMER, key: 'is_customer', type: 'main' },
  { header: HEADERS.IS_VENDOR, key: 'is_vendor', type: 'main' },
  { header: HEADERS.ADDRESS, key: 'address', type: 'main' },
  { header: HEADERS.TAX_CODE, key: 'tax_code', type: 'main' },
  { header: HEADERS.CONTACT_PERSON, key: 'contact_person', type: 'main' },
  { header: HEADERS.SALES_REP_CODE, key: 'ma_nvbh', type: 'main' },
  { header: HEADERS.ACCOUNT_CODE, key: 'tk', type: 'main' },
  { header: HEADERS.PAYMENT_TERM_CODE, key: 'ma_tt', type: 'main' },
  { header: HEADERS.CREDIT_LIMIT, key: 'credit_limit', type: 'main' },
  { header: HEADERS.BIRTH_DATE, key: 'birth_date', type: 'main' },
  { header: HEADERS.CUSTOMER_GROUP1_CODE, key: 'nh_kh1', type: 'main' },
  { header: HEADERS.CUSTOMER_GROUP2_CODE, key: 'nh_kh2', type: 'main' },
  { header: HEADERS.CUSTOMER_GROUP3_CODE, key: 'nh_kh3', type: 'main' },
  { header: HEADERS.REGION_CODE, key: 'khu_vuc', type: 'main' },
  { header: HEADERS.PHONE, key: 'phone', type: 'main' },
  { header: HEADERS.FAX, key: 'fax', type: 'main' },
  { header: HEADERS.EMAIL, key: 'email', type: 'main' },
  { header: HEADERS.WEBSITE, key: 'website', type: 'main' },
  { header: HEADERS.NOTES, key: 'notes', type: 'main' },
  { header: HEADERS.BANK_ACCOUNT, key: 'bank_account', type: 'main' },
  { header: HEADERS.BANK_NAME, key: 'bank_name', type: 'main' },
  { header: HEADERS.BANK_BRANCH, key: 'bank_branch', type: 'main' },
  { header: HEADERS.PROVINCE_CODE, key: 'province', type: 'main' },
  { header: HEADERS.USE_EINVOICE, key: 'use_einvoice', type: 'main' },
  { header: HEADERS.CUSTOMER_TYPE, key: 'customer_type', type: 'main' },
  { header: HEADERS.PAYMENT_METHOD_CODE, key: 'ma_pttt', type: 'main' },
  { header: HEADERS.EINVOICE_EMAIL, key: 'einvoice_email', type: 'main' },
  { header: HEADERS.LEGAL_REPRESENTATIVE, key: 'legal_representative', type: 'main' },
  { header: HEADERS.ID_NUMBER, key: 'id_number', type: 'main' },
  { header: HEADERS.REPRESENTATIVE_POSITION, key: 'representative_position', type: 'main' },
  { header: HEADERS.DESCRIPTION, key: 'description', type: 'main' }
];

const parse = {
  string(val: unknown): string | null {
    if (val == null || String(val).trim() === '') return null;
    return String(val).trim();
  },

  number(val: unknown, def = 0): number {
    if (val == null || String(val).trim() === '') return def;
    const n = parseFloat(String(val));
    return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
  },

  boolean(val: unknown, def = false): boolean {
    if (val == null || String(val).trim() === '') return def;
    return ['1', 'true', 'TRUE', 'True'].includes(String(val));
  },

  date(val: unknown): string | null {
    if (!val) return null;
    const str = String(val).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    const date = new Date(str);
    return isNaN(date.getTime())
      ? null
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
          2,
          '0'
        )}`;
  }
};

type ExcelFieldRows = Record<string, unknown[]>;
type UUIDLookupResponse = { main?: Record<string, Array<string | null | undefined>> };

const UUID_FIELDS = {
  main: ['ma_nvbh', 'tk', 'ma_tt', 'ma_pttt', 'nh_kh1', 'nh_kh2', 'nh_kh3', 'khu_vuc']
};

const getUUID = (uuid: unknown, field: string, i: number): string => {
  const value = (uuid as UUIDLookupResponse | null)?.main?.[field]?.[i];
  return value ? String(value) : '';
};

const UUIDPayload = (data: ParsedExcelData) => ({
  main: Object.fromEntries(
    UUID_FIELDS.main.map(f => [
      f,
      (data.main?.[f] || []).map(v => (v == null || String(v).trim() === '' ? '' : String(v).trim()))
    ])
  )
});

const getFieldName = (key: string) =>
  HEADER_CONFIG.find(c => c.key === key)
    ?.header?.replace('*', '')
    .trim() || key;

const ValidateUUID = (response: unknown, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  const main = (response as UUIDLookupResponse | null)?.main;
  if (!main) return [];

  const errors: ValidationError[] = [];

  Object.entries(main).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;

    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');
    if (!mapping) return;

    values.forEach((val, i) => {
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          section: 'main',
          columnIndex: mapping.index,
          message: `${getFieldName(field)} không hợp lệ`
        });
      }
    });
  });

  return errors;
};

type ImportBody = NhaCungCapInput;

const REQUIRED_FIELDS = ['customer_code', 'customer_name'] as const;

const hasRequired = (main: ExcelFieldRows, i: number) =>
  REQUIRED_FIELDS.every(f => main[f]?.[i] != null && String(main[f][i]).trim() !== '');

const TransformToBodies = (rows: unknown[][], parsed: ParsedExcelData, uuid: unknown): ImportBody[] => {
  const main = (parsed.main ?? {}) as ExcelFieldRows;

  return rows.reduce<ImportBody[]>((acc, _, i) => {
    if (!hasRequired(main, i)) return acc;

    const customer_code = parse.string(main.customer_code[i]);
    const customer_name = parse.string(main.customer_name[i]);
    if (!customer_code || !customer_name) return acc;

    acc.push({
      customer_code,
      customer_name,
      description: parse.string(main.description?.[i]) || '',
      is_customer: parse.boolean(main.is_customer?.[i], false),
      is_vendor: parse.boolean(main.is_vendor?.[i], true),
      customer_type: parse.string(main.customer_type?.[i]) || '2',
      alternative_name: parse.string(main.alternative_name?.[i]),
      address: parse.string(main.address?.[i]),
      tax_code: parse.string(main.tax_code?.[i]),
      contact_person: parse.string(main.contact_person?.[i]),
      sales_rep: getUUID(uuid, 'ma_nvbh', i),
      account: getUUID(uuid, 'tk', i),
      payment_term: getUUID(uuid, 'ma_tt', i),
      payment_method: getUUID(uuid, 'ma_pttt', i),
      customer_group1: getUUID(uuid, 'nh_kh1', i),
      customer_group2: getUUID(uuid, 'nh_kh2', i),
      customer_group3: getUUID(uuid, 'nh_kh3', i),
      region: getUUID(uuid, 'khu_vuc', i),
      phone: parse.string(main.phone?.[i]),
      fax: parse.string(main.fax?.[i]),
      email: parse.string(main.email?.[i]),
      website: parse.string(main.website?.[i]),
      notes: parse.string(main.notes?.[i]),
      credit_limit: parse.number(main.credit_limit?.[i]),
      birth_date: parse.date(main.birth_date?.[i]),
      id_number: parse.string(main.id_number?.[i]),
      legal_representative: parse.string(main.legal_representative?.[i]),
      representative_position: parse.string(main.representative_position?.[i]),
      bank_account: parse.string(main.bank_account?.[i]),
      bank_name: parse.string(main.bank_name?.[i]),
      bank_branch: parse.string(main.bank_branch?.[i]),
      province: parse.string(main.province?.[i])
    });

    return acc;
  }, []);
};

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};

export type { ImportBody };
